// /**
//  * Products - list
//  */

// React
import React, { useContext } from 'react';
import Can from '../../../components/Can';
// UI
import theme from '../../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Checkbox,
} from '@material-ui/core';

// Type
import { Product } from '../interfaces';
import { Link, useHistory } from 'react-router-dom';
import AuthContext from '../../../authContext';
import stableSort from '../../../utils/Table/stableSort';
import getComparator from '../../../utils/Table/getComparator';
import RonadIcons from '../../../components/iconComponent';

type Data = Product;
type Props = {
  data: any;
  handleOpenConfirmDisable: (id: number) => void;
  handleOpen: (id: number, type: string) => void;
  changeStatus: any;
};

type Ord = 'asc' | 'desc';

interface HeadCell {
  id: keyof Data;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      textAlign: 'left',
      backgroundColor: '#d3d6e8 !important',
      color: '#212121',
    },
    body: {
      textAlign: 'left',
      fontSize: 14,
      paddingLeft: '25px',
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      border: "1px solid #2F485833",
      borderRadius: "4px",
      boxShadow: "-4px 4px 12px rgba(181, 181, 181, 0.25)"
    },
    columnText: {
      padding: '0 10px',
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    hover: {
      '&:hover': {
        backgroundColor: '#F8F9FA !important',
      },
    },
  })
);

const IconSort = (order: Ord) => {
  return (
    <RonadIcons name={order === 'asc' ? 'chevronDown' : 'chevronUp'} size={1} />
  );
};

function EnhancedTableHead(props: EnhancedTableProps) {
  const authContext = useContext(AuthContext);
  const { order, orderBy, onRequestSort } = props;
  const classes = useStyles();
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };

  let headCells: HeadCell[] = [
    {
      id: 'name',
      label: 'نام کالا',
    },
    {
      id: 'code',
      label: 'کد کالا',
    },
    {
      id: 'id',
      label: 'شناسه کالا',
    },
    {
      id: 'categoryName',
      label: 'دسته بندی',
    },
    {
      id: 'sumOfLocated',
      label: 'موجودی شلف',
    },
    {
      id: 'sumOfAvailable',
      label: 'موجودی در دسترس',
    },
  ];

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell style={{paddingRight: "25px"}}>
          <Checkbox color='primary' onChange={props.selectAll}/>
        </StyledTableCell>
        {headCells.map(headCell => {
          let orderStatus = orderBy === headCell.id ? order : 'asc';
          return (
            <StyledTableCell
              key={headCell.id}
              align="center"
              sortDirection={orderBy === headCell.id ? order : false}
              data-cy={headCell.id}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderStatus}
                onClick={createSortHandler(headCell.id)}
                IconComponent={() => IconSort(orderStatus)}
              >
                <span className={classes.columnText}>{headCell.label}</span>
              </TableSortLabel>
            </StyledTableCell>
          );
        })}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const history = useHistory();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('sumOfLocated');
  const [dense] = React.useState(false);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <TableContainer className={classes.table}>
        <Table
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
            selectAll={props.selectAll}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow className={classes.hover} key={index}>
                    <StyledTableCell align="center">
                      <Checkbox color='primary' checked={props.selectedItems.includes(row.id)} onChange={e => props.selectItem(e, row.id)}/>
                    </StyledTableCell>
                    <StyledTableCell
                      data-cy="name"
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.name}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      data-cy="code"
                      scope="row"
                      align="center"
                    >
                      {row.code}
                    </StyledTableCell>
                    <StyledTableCell data-cy="id" align="center">
                      {row.id}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      data-cy="categoryName"
                      scope="row"
                      align="center"
                    >
                      {row.categoryName}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      data-cy="sumOfLocated"
                      scope="row"
                      align="center"
                    >
                      {row.sumOfLocated}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      data-cy="sumOfAvailable"
                      scope="row"
                      align="center"
                    >
                      {row.sumOfAvailable}
                    </StyledTableCell>
                    <StyledTableCell style={{ textAlign: 'center' }}>
                      <>
                        <Can
                          perform="product:edit"
                          role={authContext.user.role}
                          yes={() => (
                            <>
                              <Link
                                to={`/edit-product/${row.id}`}
                                id="editProductIcon"
                              >
                                <RonadIcons className='mr-2' name='pencil' title='ویرایش'/>
                              </Link>
                              <Link to={`/your-products/${row.id}`}>
                                <RonadIcons className='text-green' name='documentOutline' title='جزئیات'/>
                              </Link>
                            </>
                          )}
                        />
                        <RonadIcons size={.9} className='ml-2 pointer' name='timeArrow' title='تاریخچه' onClick={() => props.handleReport(row.id)} />
                      </>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
